import {Component, OnInit, ViewChild} from '@angular/core';
import {map, takeUntil} from 'rxjs/operators';
import {MatTableDataSource} from '@angular/material/table';
import {MatSort} from '@angular/material/sort';
import {ProtocolAdminService} from '../../services/api/protocol-admin.service';
import {SerialCodeTableDataViewModel} from '../../viewmodels/serialCodeTableDataViewModel';
import {AdminReportGroupFilter} from '../../viewmodels/adminReportGroupFilter';
import {BaseComponent} from '../base.component';
import {LoadingState, EmailValidator} from 'common';
import {ProtocolFindBySensorDataViewModel} from '../../viewmodels/protocolFindBySensorReportViewModel';
import {ProtocolFindBySensorDataWrapper} from '../../wrappers/protocolFindBySensorDataWrapper';
import {SerialCodeInfoDto} from '../../dtos/serialCodeInfoDto';
import {DialogShowDetailedMessageComponent} from '../dialogs/dialog-show-detailed-message';
import {MatDialog, MatDialogRef} from '@angular/material/dialog';
import {removeWhitespaces} from '../../utils/admin-utils';

@Component({
    selector: 'app-serial-code',
    templateUrl: './find-sensor-latest-usage.component.html',
    styleUrls: ['./find-sensor-latest-usage.component.scss']
})
export class FindSensorLatestUsageComponent extends BaseComponent implements OnInit {

    public showSearch: boolean;
    public showEmailSerialCodeSearch: boolean;
    public readonly dataSource: MatTableDataSource<ProtocolFindBySensorDataViewModel>;
    public columns: Map<string, string>;
    public loadingState: LoadingState;
    public viewModel: SerialCodeTableDataViewModel;
    public filter: AdminReportGroupFilter;

    @ViewChild(MatSort)
    public sort: MatSort;

    public serialCodesValue: string;
    public emailValue: string;
    public serialCodeValue: string;
    public searchSerialCodesHint: string;
    public emailHint: string;
    public serialCodeHint: string;
    public readonly minLength = 5;
    public readonly maxLength = 39;

    constructor(private serialCodeService: ProtocolAdminService, public dialog: MatDialog) {
        super();
        this.dataSource = new MatTableDataSource([]);
        this.serialCodesValue = '';
        this.serialCodeValue = '';
        this.emailValue = '';
        this.searchSerialCodesHint = 'e.g. P104210,D144412,H131323,D130456,P123456';
        this.emailHint = 'Email ...';
        this.serialCodeHint = 'Serial code ...';
        this.showSearch = true;
        this.showEmailSerialCodeSearch = true;

        this.columns = new Map<string, string>();
        this.columns.set('position', 'No');
        this.columns.set('serialCode', 'Serial Code');
        this.columns.set('firstTimeUsedDate', 'First Usage');
        this.columns.set('lastTimeUsedDate', 'Last Usage');
        this.columns.set('email', 'Email');

        this.filter = new AdminReportGroupFilter();
        this.filter.supportsQuery = false;
        this.filter.supportsSubscriptionPlanTypes = false;
        this.filter.supportsSubscriptionPeriodUnits = false;
        this.filter.supportsSubscriptionSources = false;
    }

    ngOnInit(): void {
        this.analyticsSwitchPage('Admin-FindSensorLatestUsage', '', '/admin/findSensorLatestUsage');
    }

    onSubmit(): void {
        this.search();
    }

    onSerialCodeAndEmailSubmit(): void {
        this.searchBySerialCodeAndEmail();
    }

    onSerialCodesValueChange(value: string): void {
        const cleanValue = removeWhitespaces(value);

        this.serialCodesValue = cleanValue.toUpperCase();
    }

    onSerialCodeValueChange(value: string): void {
        const cleanValue = removeWhitespaces(value);

        this.serialCodeValue = cleanValue.toUpperCase();
    }

    onEmailValueChange(value: string): void {
        const cleanValue = removeWhitespaces(value);

        this.emailValue = cleanValue.toUpperCase();
    }

    canSearch(): boolean {
        return this.showSearch &&
            this.serialCodesValue?.length >= this.minLength &&
            this.emailValue.length === 0 &&
            this.serialCodeValue.length === 0;
    }

    canEmailSerialCodeSearch(): boolean {
        return this.showEmailSerialCodeSearch &&
            this.serialCodeValue?.length >= this.minLength &&
            EmailValidator.isValid(this.emailValue) &&
            this.serialCodesValue?.length === 0;
    }

    showSerialCodeError(): boolean {
        return this.serialCodeValue?.length !== 0 && this.serialCodeValue?.length < this.minLength;
    }

    showEmailValueError(): boolean {
        return this.emailValue.length !== 0 && !EmailValidator.isValid(this.emailValue);
    }

    private search(): void {
        this.loadingState = LoadingState.LOADING;
        this.showSearch = false;
        this.showEmailSerialCodeSearch = false;
        const serialCodes = this.serialCodesValue.split(',');

        this.serialCodeService.findLatestUsageForSerialCodes(serialCodes)
            .pipe(takeUntil(this.destroySubject))
            .subscribe((serialCodeInfos: SerialCodeInfoDto[]) => {
                const dataWrapper: ProtocolFindBySensorDataWrapper[] = [];

                if (serialCodeInfos.length === 0) {
                    // No results found
                    this.finalizeSearch(dataWrapper);
                    return;
                }

                serialCodeInfos.forEach((value, index) => {
                    dataWrapper.push(new ProtocolFindBySensorDataWrapper(index, value.serialCode,
                        value.userCode, value.email, value.lastTimeUsed, value.firstTimeUsed));

                    this.finalizeSearch(dataWrapper);
                });

            }, error => {
                this.loadingState = LoadingState.ERROR;
                this.showSearch = true;
                this.showEmailSerialCodeSearch = true;
                let message;
                if (error.status === 406) {
                    message = 'The system searches lots of serial codes right now.\n ' +
                        'Please try again later or use the fast search option.';
                } else if (error.status === 401) {
                    message = 'Your session has expired. Please login again.';
                } else {
                    message = 'The system has encountered an error. \n Please try again later.';
                }
                this.openDialog(message);
                this.errorHandler.handle(error);
            });
    }

    private searchBySerialCodeAndEmail(): void {
        this.loadingState = LoadingState.LOADING;
        this.showSearch = false;
        this.showEmailSerialCodeSearch = false;

        this.serialCodeService.findLatestUsageForSerialCodeAndEmail(this.serialCodeValue, this.emailValue)
            .pipe(takeUntil(this.destroySubject))
            .pipe(map((serialCodeInfo: SerialCodeInfoDto) => {
                return new ProtocolFindBySensorDataWrapper(0, this.serialCodeValue,
                    '', this.emailValue, serialCodeInfo.lastTimeUsed, serialCodeInfo.firstTimeUsed);
            }))
            .subscribe((result) => {
                this.finalizeSearch([result]);
            }, error => {
                this.loadingState = LoadingState.ERROR;
                this.showSearch = true;
                this.showEmailSerialCodeSearch = true;
                let message;
                if (error.status === 401) {
                    message = 'Your session has expired. Please login again.';
                } else if (error.status === 404) {
                    message = 'Email ' + this.emailValue + ' cannot match to an account.\n' +
                        'Please provide a correct email address that matches an account.';
                } else {
                    message = 'Something went wrong during sensor usage information search.\n' +
                        'Please make sure that your serial code and email are the correct ones and try again in a few.';
                }
                this.openDialog(message);
                this.errorHandler.handle(error);
            });
    }

    openDialog(message: string): void {
        const dialogRef: MatDialogRef<DialogShowDetailedMessageComponent> =
            this.dialog.open(DialogShowDetailedMessageComponent, {
                disableClose: true,
                data: {message, title: 'Sensor Usage Information', needsCloseButton: true}
            });

        dialogRef.componentInstance.closeDialog.subscribe(() => {
            dialogRef.close();
        });
    }

    private finalizeSearch(dataWrapper: ProtocolFindBySensorDataWrapper[]): void {
        this.dataSource.data = ProtocolFindBySensorDataViewModel.from(dataWrapper);
        this.loadingState = LoadingState.LOADED;
        this.showSearch = true;
        this.showEmailSerialCodeSearch = true;
    }
}
